<template>
  <div class="form-field-wrp">
    <label
      v-if="label && !hidelabel"
      v-tooltip="labelTooltip"
      class="label"
      :class="{ readonly, widefield }"
    >
      <div ref="labelTextRef">
        <span>{{ label }}</span>
      </div>
      <span class="icons is-size-7">
        <span
          v-if="isEditing && mandatory"
          v-tooltip="'This field is mandatory'"
          class="icon is-small mandatory"
        >
          <i class="fa-solid fa-asterisk fa-xs" />
        </span>
        <span
          v-if="isEditing && readonly"
          v-tooltip="'This field is read-only'"
          class="icon is-small readonly"
        >
          <i class="fa-solid fa-lock fa-xs" />
        </span>
        <HelpIcon :label="label" :text="helpText" />
      </span>
    </label>
    <div
      ref="valueRef"
      class="field-value"
      :class="{ editing: isEditing, widevalue, readonly }"
    >
      <slot v-bind="{ ...$props, ...$attrs }">
        <em class="has-text-grey">&lt;value&gt;</em>
      </slot>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useOverflowTooltip } from '@/composables/useOverflowTooltip'

import HelpIcon from '@/components/ui/HelpIcon.vue'

export default {
  name: 'ItemDetailField',
  components: { HelpIcon },
  props: {
    isEditing: { type: Boolean, default: false },
    label: { type: String },
    description: { type: String },
    mandatory: { type: Boolean },
    readonly: { type: Boolean },
    help: { type: String, default: null },
    hidelabel: { type: Boolean, default: false },
    widefield: { type: Boolean },
    widevalue: { type: Boolean, default: false },
  },
  setup(props) {
    const { elementRef, isOverflowing } = useOverflowTooltip({
      tooltipContent: () => props.label,
    })

    // Label tooltip: only for overflow (truncated text)
    const labelTooltip = computed(() => {
      if (isOverflowing.value) return props.label
      return undefined
    })

    // Help icon: shows description or explicit help text
    const helpText = computed(() => props.help || props.description)

    // Gently fade the value when it changes under the user (e.g. a silent
    // record refresh after linking an ontology term) so updates aren't missed.
    // Watches the rendered content rather than a value prop, since the value
    // lives in the slot. Armed shortly after mount to skip the initial render,
    // and ignored while editing (input swaps aren't "value changed").
    const valueRef = ref(null)
    let observer = null
    let pulseTimer = null
    let armTimer = null
    let armed = false

    function pulseUpdated() {
      const el = valueRef.value
      if (!el) return
      el.classList.remove('field-value--updated')
      void el.offsetWidth // force reflow so the animation restarts
      el.classList.add('field-value--updated')
    }

    // Briefly disarm so the burst of mutations from a render or an edit↔view
    // widget swap doesn't pulse — only genuine later changes do.
    function arm() {
      armed = false
      clearTimeout(armTimer)
      armTimer = setTimeout(() => (armed = true), 400)
    }

    onMounted(() => {
      const el = valueRef.value
      if (!el || typeof MutationObserver === 'undefined') return
      observer = new MutationObserver(() => {
        if (!armed || props.isEditing) return
        clearTimeout(pulseTimer)
        pulseTimer = setTimeout(pulseUpdated, 30)
      })
      observer.observe(el, {
        childList: true,
        subtree: true,
        characterData: true,
      })
      arm()
    })

    // Entering/leaving edit mode swaps the input for the value widget — re-arm
    // so that swap isn't mistaken for a value change.
    watch(() => props.isEditing, arm)

    onBeforeUnmount(() => {
      observer?.disconnect()
      clearTimeout(pulseTimer)
      clearTimeout(armTimer)
    })

    return {
      labelTextRef: elementRef,
      labelTooltip,
      helpText,
      valueRef,
    }
  },
}
</script>

<style lang="scss" scoped>
// Subtle fade-out → fade-in when a field's value changes under the user.
.field-value--updated {
  animation: field-value-refresh var(--transition-slow);
}
@keyframes field-value-refresh {
  0% {
    opacity: 1;
  }
  40% {
    opacity: 0.15;
  }
  100% {
    opacity: 1;
  }
}

div.form-field-wrp {
  display: contents;

  label {
    justify-self: start;
    align-self: start;
    margin-bottom: 0;
    white-space: nowrap;
    display: flex;
    padding-right: var(--space-3);
    min-width: 70px;
    overflow: hidden;
    &.label {
      color: var(--text-muted);
      font-family: var(--font-label);
      font-size: var(--text-base);
      font-weight: var(--font-normal);
      letter-spacing: 0;
      line-height: 1.4;
    }

    & > div:first-child {
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }

    span.icons {
      opacity: 0.8;
      transform: translateY(-5px);
      margin-left: 5px;
      flex-shrink: 0;
    }

    span.icon.mandatory {
      color: var(--danger);
      opacity: 1;
    }

    // The tall-editing-input top nudge lives in the field-side-by-side mixin
    // (detail-field-grid.scss) so it applies exactly when the label is inline.
  }

  div.field-value {
    display: flex;
    grid-area: field-value;
    flex-grow: 1;
    word-break: break-word;
    max-width: 100%;
    min-width: 0;
    font-size: var(--text-base);
    font-weight: var(--font-medium);
    color: var(--text-default);
    &.editing,
    &.readonly {
      align-items: center;
    }
    &.editing:not(.readonly),
    &.widevalue {
      :deep(> *) {
        flex: 1;
      }
      // Native selects should size to content, not stretch
      :deep(> .select) {
        flex: 0 1 auto;
        min-width: 0;
      }
    }
  }
}
</style>
